import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { QrCode } from 'lucide-react'
import { useState } from 'react'

import { PageBackButton } from '@/components/common/PageBackButton'
import { GrowerAreaQRTrigger } from '@/components/growers/grower-area-qr-trigger'
import { GrowerQRDialog } from '@/components/growers/grower-qr-dialog'
import { GrowerReadonlyView } from '@/components/growers/grower-readonly-view'
import { GrowerEditPage } from '@/components/growers/grower-edit-page'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { useGrower } from '@/hooks/use-growers-query'
import { ApiError } from '@/lib/api/client'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agriculture/growers/$id',
)({
    component: GrowerDetailRoutePage,
})

/** Chỉ 404 mới là "hộ không tồn tại"; lỗi khác là sự cố tải, không được báo
 *  nhầm thành thiếu dữ liệu. */
function isNotFound(error: unknown): boolean {
    return error instanceof ApiError && error.status === 404
}

/**
 * Trang hồ sơ hộ trồng. Có quyền ghi thì mở thẳng màn sửa — nhân viên vào đây
 * hầu như luôn để cập nhật, thêm một nhịp bấm "Sửa" chỉ tổ vướng. Không có
 * quyền thì vẫn xem được, chỉ đọc.
 */
export function GrowerDetailRoutePage() {
    const { id } = useParams({
        from: '/_app/_authed/_sidebar/agriculture/growers/$id',
    })
    const navigate = useNavigate()
    const [qrOpen, setQrOpen] = useState(false)
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('growers.write')
    const { data: grower, isLoading, isError, error, refetch } = useGrower(id)
    const notFound = isError && isNotFound(error)
    const failed = isError && !notFound

    // Chờ hộ về rồi mới dựng màn sửa: form nhận dữ liệu qua `values` nên khi
    // response tới nó reset toàn bộ ô nhập — ai gõ trong lúc còn trắng sẽ mất
    // chữ vừa gõ, mà form trắng cũng khiến người dùng tưởng hộ không có dữ liệu.
    if (canWrite && isLoading) {
        return (
            <div className="page-pad">
                <p className="text-sm text-muted-foreground">
                    {m.common_loading()}
                </p>
            </div>
        )
    }

    // Màn sửa tự lo tiêu đề và nút Huỷ/Lưu, nên không bọc thêm khung xem ở
    // ngoài — tránh hai tiêu đề chồng nhau.
    if (canWrite && !notFound && !failed) {
        return (
            <div className="page-pad">
                <GrowerEditPage
                    mode="edit"
                    growerId={id}
                    onSaved={() =>
                        void navigate({ to: '/agriculture/growers' })
                    }
                />
            </div>
        )
    }

    return (
        <div className="space-y-4 page-pad">
            <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                    <PageBackButton
                        onBack={() =>
                            void navigate({ to: '/agriculture/growers' })
                        }
                    />
                    <div>
                        <h1 className="text-xl font-semibold md:text-2xl">
                            {grower?.name ?? m.grower_title()}
                        </h1>
                        <p className="mt-0.5 text-sm text-muted-foreground">
                            {m.grower_page_subtitle()}
                        </p>
                    </div>
                </div>

                {grower ? (
                    <div className="flex shrink-0 items-center gap-2">
                        <GrowerAreaQRTrigger
                            areas={grower.areas}
                            growerName={grower.name}
                            growerPhone={grower.phone}
                            entry="grower_detail"
                        />
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="shrink-0"
                            onClick={() => setQrOpen(true)}
                            title={m.grower_qr_button()}
                            aria-label={m.grower_qr_button()}
                        >
                            <QrCode className="size-4" />
                        </Button>
                    </div>
                ) : null}
            </div>

            {isLoading ? (
                <p className="text-sm text-muted-foreground">
                    {m.common_loading()}
                </p>
            ) : null}

            {notFound ? (
                <p className="rounded-2xl border bg-card p-5 text-sm text-muted-foreground">
                    {m.grower_not_found()}
                </p>
            ) : null}

            {failed ? (
                <div className="space-y-3">
                    <p className="rounded-2xl border bg-card p-5 text-sm text-muted-foreground">
                        {m.my_grower_load_error()}
                    </p>
                    <Button type="button" onClick={() => void refetch()}>
                        {m.my_grower_retry()}
                    </Button>
                </div>
            ) : null}

            {grower ? (
                <GrowerReadonlyView grower={grower} scope="admin" />
            ) : null}

            <GrowerQRDialog
                open={qrOpen}
                onOpenChange={setQrOpen}
                growerName={grower?.name}
                phone={grower?.phone}
            />
        </div>
    )
}
